import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Search, Package, MessageSquare, Mail, Star, X, Loader2, RefreshCw, Settings, Clock, Undo2, ShieldCheck, AlertTriangle, RotateCcw, CalendarDays, Copy, Pencil, Trash2, Eye, EyeOff, Plus, Save, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { BRAND } from "../../data/content";
import MenuManagement from "./MenuManagement";
import AnalyticsPanel from "./AnalyticsPanel";

const ORDER_STATUSES = ["New","Pending Review","Awaiting Customer Response","Invoice Ready","Invoice Sent","Payment Pending","Paid","Confirmed","Scheduled","Completed","Cancelled"];
const CUSTOMER_FACING_STATUSES = ["Paid","Confirmed","Scheduled"];
const FRIDAY_STATUSES = ["New","Pending Review","Awaiting Payment","Paid","Confirmed","Preparing","Ready for Pickup","Out for Delivery","Completed","Cancelled"];
const FRIDAY_CUSTOMER_FACING = ["Paid","Confirmed","Ready for Pickup","Out for Delivery"];
const FRIDAY_INVOICE_STATUSES = ["Not Sent","Sent"];
const FRIDAY_PAYMENT_STATUSES = ["Pending","Paid"];
const FRIDAY_PLATE_STATES = ["Available","Sold Out","Hidden"];
const INVOICE_STATUSES = ["Not Started","Invoice Ready","Invoice Sent","Paid"];
const PAYMENT_STATUSES = ["Unpaid","Payment Pending","Paid"];
const INQUIRY_STATUSES = ["New","Under Review","Contacted","Quote Sent","Awaiting Customer Response","Booked","Completed","Closed"];
const CONTACT_STATUSES = ["New","Under Review","Contacted","Closed"];

const STATUS_COLOR = {
  "New": "bg-slate-100 text-slate-700",
  "Pending Review": "bg-amber-100 text-amber-800",
  "Awaiting Customer Response": "bg-orange-100 text-orange-800",
  "Invoice Ready": "bg-blue-100 text-blue-800",
  "Invoice Sent": "bg-indigo-100 text-indigo-800",
  "Payment Pending": "bg-purple-100 text-purple-800",
  "Paid": "bg-green-100 text-green-800",
  "Confirmed": "bg-emerald-100 text-emerald-800",
  "Scheduled": "bg-teal-100 text-teal-800",
  "Completed": "bg-green-100 text-green-800",
  "Cancelled": "bg-red-100 text-red-700",
};

const TABS = [
  { key: "orders", label: "Meal Prep Orders", icon: Package },
  { key: "friday", label: "Friday Plate Orders", icon: CalendarDays },
  { key: "inquiries", label: "Catering Inquiries", icon: MessageSquare },
  { key: "contacts", label: "Messages", icon: Mail },
  { key: "content", label: "Content", icon: Settings },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState({});

  const loadStats = useCallback(() => { api.get("/admin/stats").then((r)=>setStats(r.data)).catch(()=>{}); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-bone">
      <header className="bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Taste of Platinum" className="w-9 h-9 rounded-full object-cover ring-1 ring-gold/40" />
            <span className="font-serif text-xl font-semibold text-white">Taste of <span className="gold-text">Platinum</span> <span className="text-sm text-platinum-dark font-sans">· Admin</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-platinum">{user?.email}</span>
            <button data-testid="admin-logout" onClick={doLogout} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/10">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Orders" value={stats.total_orders} testid="stat-total-orders" />
          <StatCard label="Pending Review" value={stats.pending_orders} testid="stat-pending-orders" accent />
          <StatCard label="Inquiries" value={stats.total_inquiries} testid="stat-inquiries" />
          <StatCard label="Messages" value={stats.total_contacts} testid="stat-contacts" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto thin-scroll">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition ${tab===t.key?"bg-charcoal text-white":"bg-white border border-slate-200 text-slate-600"}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "orders" && <Orders onChange={loadStats} />}
        {tab === "friday" && <FridayOrders onChange={loadStats} />}
        {tab === "inquiries" && <Inquiries />}
        {tab === "contacts" && <Contacts />}
        {tab === "content" && <Content />}
        {tab === "analytics" && <AnalyticsPanel />}
      </div>
    </div>
  );
}

function StatCard({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className={`rounded-2xl p-5 ${accent?"bg-gold text-charcoal":"bg-white border border-slate-200"}`}>
      <div className="text-3xl font-serif font-semibold">{value ?? "—"}</div>
      <div className={`text-sm ${accent?"text-charcoal/70":"text-slate-500"}`}>{label}</div>
    </div>
  );
}

function Orders({ onChange }) {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status !== "All") params.status = status;
    api.get("/admin/orders", { params }).then((r)=>setOrders(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [search, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input data-testid="orders-search" placeholder="Search by Order ID, name, email, phone…" value={search} onChange={(e)=>setSearch(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-2.5 outline-none focus:border-gold" />
        </div>
        <select data-testid="orders-status-filter" value={status} onChange={(e)=>setStatus(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 outline-none focus:border-gold">
          <option>All</option>
          {ORDER_STATUSES.map((s)=><option key={s}>{s}</option>)}
        </select>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-600"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div> :
      orders.length === 0 ? <div className="py-16 text-center text-slate-400" data-testid="orders-empty">No orders found.</div> : (
        <div className="overflow-x-auto thin-scroll rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Order ID</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Package</th>
                <th className="px-4 py-3 font-medium">Fulfillment</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} data-testid={`order-row-${o.order_id}`} onClick={()=>setSelected(o.order_id)}
                  className="border-t border-slate-100 hover:bg-bone cursor-pointer">
                  <td className="px-4 py-3 font-mono font-semibold text-charcoal">{o.order_id}</td>
                  <td className="px-4 py-3">{o.first_name} {o.last_name}<div className="text-xs text-slate-400">{o.email}</div></td>
                  <td className="px-4 py-3">{o.order_type === "custom" ? "Custom" : `${o.package_meals} meals`}</td>
                  <td className="px-4 py-3">{o.fulfillment_day} · {o.fulfillment_method}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLOR[o.status]||"bg-slate-100"}`}>{o.status}</span></td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <OrderDrawer orderId={selected} onClose={()=>setSelected(null)} onSaved={()=>{load(); onChange&&onChange();}} />}
    </div>
  );
}

function OrderDrawer({ orderId, onClose, onSaved }) {
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [flow, setFlow] = useState(null);       // guided status-change modal state
  const [nowTick, setNowTick] = useState(Date.now());

  const applyOrder = (data) => {
    setOrder(data);
    setForm({
      status: data.status, invoice_status: data.invoice_status, payment_status: data.payment_status,
      final_price: data.final_price ?? "", admin_notes: data.admin_notes ?? "",
    });
  };
  const reload = useCallback(() => api.get(`/admin/orders/${orderId}`).then((r)=>applyOrder(r.data)).catch(()=>{}), [orderId]);
  useEffect(() => { reload(); }, [reload]);

  const pending = order?.pending_notification;
  const secondsLeft = pending ? Math.max(0, Math.ceil((new Date(pending.send_at).getTime() - nowTick)/1000)) : 0;

  // Countdown + poll for the send result while a notification is pending.
  useEffect(() => {
    if (!pending) return;
    const tick = setInterval(() => setNowTick(Date.now()), 1000);
    const poll = setInterval(() => { reload(); }, 4000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [pending, reload]);

  const onStatusSelect = async (v) => {
    if (CUSTOMER_FACING_STATUSES.includes(v) && v !== order.status) {
      setFlow({ step: "review", loading: true });
      try {
        const r = await api.post(`/admin/orders/${orderId}/status-change/preview`, { new_status: v });
        setFlow({ step: "review", preview: r.data, verified: false });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setFlow(null); }
    } else {
      setForm({ ...form, status: v });
    }
  };

  const commitStatus = async () => {
    const p = flow.preview;
    setFlow({ ...flow, submitting: true, error: null });
    try {
      await api.post(`/admin/orders/${orderId}/status-change/commit`, { new_status: p.new_status, payment_verified: !!flow.verified });
      setFlow(null);
      toast.success(`Order marked ${p.new_status}. Customer email will send in ${p.grace_seconds}s — you can still UNDO.`);
      await reload();
      onSaved && onSaved();
    } catch (e) { setFlow({ ...flow, submitting: false, error: formatApiError(e.response?.data?.detail) }); }
  };

  const undo = async () => {
    try {
      await api.post(`/admin/orders/${orderId}/status-change/undo`);
      toast.success("Status change undone. No email was sent.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    await reload(); onSaved && onSaved();
  };

  const retryEmail = async () => {
    if (!window.confirm("Resend the customer email for this status? An email will be attempted again.")) return;
    try {
      await api.post(`/admin/orders/${orderId}/status-change/retry`);
      toast.success("Customer email resent.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    await reload(); onSaved && onSaved();
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, final_price: form.final_price === "" ? null : parseFloat(form.final_price) };
      await api.patch(`/admin/orders/${orderId}`, payload);
      toast.success("Order updated");
      onSaved();
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const mailto = order ? `mailto:${order.email}?subject=${encodeURIComponent(`Taste of Platinum — Order ${order.order_id}`)}` : "#";
  const history = order?.status_history || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-drawer">
      <div className="absolute inset-0 bg-charcoal/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-bone h-full overflow-y-auto thin-scroll shadow-2xl animate-fade-in">
        <div className="sticky top-0 z-10 bg-charcoal text-white px-6 py-4 flex items-center justify-between">
          <div><div className="font-mono font-semibold">{orderId}</div><div className="text-xs text-platinum-dark">Order details</div></div>
          <button data-testid="drawer-close" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        {!order ? <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div> : (
          <div className="p-6 space-y-6">
            {pending && (
              <div data-testid="pending-notification-banner" className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4">
                <div className="flex items-center gap-2 text-amber-900 font-semibold">
                  <Clock className="w-5 h-5" /> Order marked {order.status}.
                </div>
                <p className="mt-1 text-sm text-amber-800">
                  Customer Notification: <strong data-testid="pending-countdown">Pending — sends in {secondsLeft} second{secondsLeft===1?"":"s"}</strong>
                </p>
                <button data-testid="undo-status-change" onClick={undo}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-sm font-semibold text-white hover:bg-charcoal/90">
                  <Undo2 className="w-4 h-4" /> UNDO
                </button>
              </div>
            )}
            <Section title="Customer">
              <Row k="Name" v={`${order.first_name} ${order.last_name}`} />
              <Row k="Email" v={order.email} />
              <Row k="Phone" v={order.phone} />
              <a data-testid="email-customer-shortcut" href={mailto}
                 className="inline-flex items-center gap-2 mt-1 rounded-full border border-slate-300 px-4 py-2 text-sm text-charcoal hover:bg-bone">
                <Mail className="w-4 h-4" /> Email Customer
              </a>
            </Section>
            <Section title="Order">
              <Row k="Type" v={order.order_type === "custom" ? "Single Meal / Custom Order" : `${order.package_meals} Meal Package · ${order.total_meals ?? order.meals?.reduce((a,m)=>a+(m.quantity||0),0)} meals built`} />
              {order.meals?.length > 0 && (
                <div className="pt-1">
                  <div className="text-xs text-slate-400 mb-1">Meals (entrée + included sides)</div>
                  <div className="space-y-2">
                    {order.meals.map((m, i) => (
                      <div key={i} data-testid={`drawer-meal-${i}`} className="rounded-lg bg-bone p-2.5 text-sm">
                        <div className="font-semibold text-charcoal">{m.entree} × {m.quantity}</div>
                        <div className="text-slate-500 text-xs">Sides: {m.sides?.length ? m.sides.join(", ") : "None"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {order.custom_request && <Row k="Custom Request" v={order.custom_request} />}
              {order.additional_sides?.length > 0 && <Row k="Additional Sides" v={`${order.additional_sides.map(s=>`${s.name} ×${s.quantity}`).join(", ")} ($${(order.additional_sides_total||0).toFixed(2)})`} />}
              {order.appetizers?.length > 0 && <Row k="Appetizers" v={`${order.appetizers.map(s=>`${s.name} ×${s.quantity}`).join(", ")} (Price TBD)`} />}
              {order.desserts?.length > 0 && <Row k="Desserts" v={`${order.desserts.map(s=>`${s.name} ×${s.quantity}`).join(", ")} ($${(order.desserts_total||0).toFixed(2)})`} />}
              <Row k="Fulfillment" v={`${order.fulfillment_day} · ${order.fulfillment_method}`} />
              {order.delivery && <Row k="Delivery" v={`${order.delivery.address} ${order.delivery.apt}, ${order.delivery.city} ${order.delivery.zip} (${order.delivery.window||"flexible"})`} />}
              {order.dietary && <Row k="Dietary" v={order.dietary} />}
              {order.notes && <Row k="Notes" v={order.notes} />}
              <Row k="Submitted" v={new Date(order.created_at).toLocaleString()} />
            </Section>
            <Section title="Notifications">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm text-slate-600">Customer status email:</span>
                <StatusNotifPill order={order} secondsLeft={secondsLeft} />
              </div>
              {order.status_notification === "Failed" && !pending && (
                <button data-testid="retry-status-email" onClick={retryEmail}
                  className="mt-3 inline-flex items-center gap-2 rounded-full border border-red-300 text-red-700 px-4 py-2 text-sm hover:bg-red-50">
                  <RotateCcw className="w-4 h-4" /> Retry Email
                </button>
              )}
              <div className="mt-3 border-t border-slate-100 pt-3"><EmailStatus record={order} /></div>
            </Section>
            {history.length > 0 && (
              <Section title="Status History">
                <div className="space-y-2" data-testid="status-history">
                  {[...history].reverse().map((h) => (
                    <div key={h.id} className="rounded-lg bg-bone p-2.5 text-xs text-slate-600">
                      <div className="text-slate-400">{new Date(h.at).toLocaleString()} · {h.admin || "admin"}</div>
                      <div className="font-medium text-charcoal">{h.previous_status} → {h.new_status}</div>
                      <div>{h.cancelled ? "Action cancelled during undo period · Customer notification: Not Sent"
                        : `Customer notification: ${h.email_status || (h.notification_triggered ? "Pending" : "None")}`}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            <Section title="Manage">
              <div className="space-y-3">
                <Select label="Order Status" testid="drawer-status" value={form.status} options={ORDER_STATUSES} disabled={!!pending} onChange={onStatusSelect} />
                {!pending && <p className="text-xs text-slate-400 -mt-1">Paid, Confirmed and Scheduled require a guided confirmation before any customer email is sent.</p>}
                <Select label="Invoice Status" testid="drawer-invoice-status" value={form.invoice_status} options={INVOICE_STATUSES} onChange={(v)=>setForm({...form,invoice_status:v})} />
                <Select label="Payment Status" testid="drawer-payment-status" value={form.payment_status} options={PAYMENT_STATUSES} onChange={(v)=>setForm({...form,payment_status:v})} />
                <label className="block"><span className="text-sm font-medium text-charcoal">Final Price ($)</span>
                  <input data-testid="drawer-final-price" type="number" step="0.01" className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-gold" value={form.final_price} onChange={(e)=>setForm({...form,final_price:e.target.value})} /></label>
                <label className="block"><span className="text-sm font-medium text-charcoal">Admin Notes</span>
                  <textarea data-testid="drawer-admin-notes" rows={3} className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-gold" value={form.admin_notes} onChange={(e)=>setForm({...form,admin_notes:e.target.value})} /></label>
              </div>
              <button data-testid="drawer-save" onClick={save} disabled={saving}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 font-semibold text-charcoal hover:bg-gold-bright disabled:opacity-60">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Changes"}
              </button>
            </Section>
          </div>
        )}
      </div>
      {flow && <StatusChangeModal flow={flow} setFlow={setFlow} onCommit={commitStatus} onClose={()=>setFlow(null)} />}
    </div>
  );
}

function StatusNotifPill({ order, secondsLeft }) {
  const s = order.status_notification;
  if (order.pending_notification)
    return <span data-testid="notif-pill" className="rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-medium">Pending — sends in {secondsLeft}s</span>;
  if (s === "Sent") return <span data-testid="notif-pill" className="rounded-full bg-green-100 text-green-800 px-2.5 py-1 text-xs font-medium">Sent</span>;
  if (s === "Failed") return <span data-testid="notif-pill" className="rounded-full bg-red-100 text-red-700 px-2.5 py-1 text-xs font-medium">Failed</span>;
  if (s === "Not Sent") return <span data-testid="notif-pill" className="rounded-full bg-slate-100 text-slate-500 px-2.5 py-1 text-xs font-medium">Not Sent</span>;
  return <span data-testid="notif-pill" className="rounded-full bg-slate-100 text-slate-500 px-2.5 py-1 text-xs font-medium">None</span>;
}

function StatusChangeModal({ flow, setFlow, onCommit, onClose }) {
  const p = flow.preview;
  const isPaid = p?.new_status === "Paid";
  const hasWarnings = (p?.warnings || []).length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="status-change-modal">
      <div className="absolute inset-0 bg-charcoal/70" />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden animate-fade-in">
        {flow.loading || !p ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div>
        ) : flow.step === "review" ? (
          <div>
            <div className="bg-charcoal text-white px-6 py-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-gold" /><h3 className="font-serif text-lg">Review Status Change</h3></div>
            <div className="p-6 space-y-3 text-sm">
              <Row k="Customer" v={p.customer_name} />
              <Row k="Order ID" v={p.order_id} />
              <Row k="Current Status" v={p.current_status} />
              <Row k="New Status" v={p.new_status} />
              <Row k="Customer Email" v={p.customer_email} />
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-amber-900 text-xs flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Changing this status will trigger an automated email to the customer after confirmation and the undo period.</span>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Email preview</div>
                <div className="text-charcoal font-medium">{p.email_subject}</div>
                <div className="text-xs text-slate-500 mt-1">To: {p.customer_name} &lt;{p.customer_email}&gt;</div>
                <div className="text-xs text-slate-500">Order: {p.order_id} · Status: {p.new_status}</div>
                {p.details?.map((d,i)=><div key={i} className="text-xs text-slate-500">{d}</div>)}
              </div>
              {hasWarnings && (
                <div data-testid="scheduling-warning" className="rounded-xl bg-red-50 border border-red-200 p-3 text-red-700 text-xs">
                  <div className="font-semibold mb-1">Missing scheduling information — fix before sending:</div>
                  <ul className="list-disc pl-4">{p.warnings.map((w,i)=><li key={i}>{w}</li>)}</ul>
                </div>
              )}
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button data-testid="status-review-goback" onClick={onClose} className="flex-1 rounded-full border border-slate-300 px-5 py-2.5 font-semibold text-charcoal hover:bg-bone">Go Back</button>
              <button data-testid="status-review-continue" disabled={hasWarnings} onClick={()=>setFlow({ ...flow, step:"confirm", verified:false, error:null })}
                className="flex-1 rounded-full bg-gold px-5 py-2.5 font-semibold text-charcoal hover:bg-gold-bright disabled:opacity-50">Continue</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="bg-charcoal text-white px-6 py-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-gold" /><h3 className="font-serif text-lg">Confirm Status Change</h3></div>
            <div className="p-6 space-y-4 text-sm">
              <p className="text-charcoal">
                You are changing Order <strong>{p.order_id}</strong> from <strong>{p.current_status}</strong> to <strong>{p.new_status}</strong>.
                After the undo period expires, the customer will automatically receive a{p.new_status==="Paid"?" payment confirmation":p.new_status==="Scheduled"?" scheduling":" confirmation"} email.
              </p>
              {isPaid && (
                <label data-testid="paid-verify-checkbox" className="flex items-start gap-2 rounded-xl bg-bone p-3 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 w-4 h-4 accent-gold" checked={!!flow.verified} onChange={(e)=>setFlow({ ...flow, verified:e.target.checked })} />
                  <span className="text-charcoal">I have verified that payment has been received.</span>
                </label>
              )}
              {flow.error && <div data-testid="status-confirm-error" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-red-700 text-xs">{flow.error}</div>}
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button data-testid="status-confirm-cancel" onClick={onClose} className="flex-1 rounded-full border border-slate-300 px-5 py-2.5 font-semibold text-charcoal hover:bg-bone">Cancel</button>
              <button data-testid="status-confirm-submit" onClick={onCommit} disabled={flow.submitting || (isPaid && !flow.verified)}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 font-semibold text-charcoal hover:bg-gold-bright disabled:opacity-50">
                {flow.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Working…</> : "Confirm Status Change"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Inquiries() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    api.get("/admin/inquiries", { params }).then((r)=>setItems(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [search]);
  useEffect(() => { const t=setTimeout(load,250); return ()=>clearTimeout(t); }, [load]);

  const update = async (id, patch) => {
    try { await api.patch(`/admin/inquiries/${id}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const shown = status === "All" ? items : items.filter((q) => q.status === status);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input data-testid="inquiries-search" placeholder="Search by ID, name, email, event…" value={search} onChange={(e)=>setSearch(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-2.5 outline-none focus:border-gold" />
        </div>
        <select data-testid="inquiries-status-filter" value={status} onChange={(e)=>setStatus(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 outline-none focus:border-gold">
          <option>All</option>
          {INQUIRY_STATUSES.map((s)=><option key={s}>{s}</option>)}
        </select>
      </div>
      {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div> :
      shown.length === 0 ? <div className="py-16 text-center text-slate-400" data-testid="inquiries-empty">No inquiries found.</div> : (
        <div className="grid gap-4 lg:grid-cols-2">
          {shown.map((q) => (
            <div key={q.request_id} data-testid={`inquiry-card-${q.request_id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold text-charcoal">{q.request_id}</span>
                <span className="text-xs text-slate-400">{new Date(q.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-2 font-semibold">{q.name}</div>
              <div className="text-sm text-slate-500">{q.email} · {q.phone}</div>
              <div className="mt-3 text-sm space-y-1 text-slate-600">
                <div><b>Event:</b> {q.event_type||"—"} · {q.event_date||"TBD"} · {q.event_time||""} · {q.guest_count||"?"} guests</div>
                <div><b>Location:</b> {q.location||"—"}</div>
                <div><b>Services:</b> {q.services?.join(", ")||"—"}</div>
                <div><b>Menu:</b> {q.menu_interests?.join(", ")||"—"}</div>
                {q.custom_dish && <div><b>Custom dish:</b> {q.custom_dish}</div>}
                <div><b>Budget:</b> {q.budget||"—"}</div>
                {q.details && <div><b>Details:</b> {q.details}</div>}
              </div>
              <div className="mt-3"><EmailStatus record={q} /></div>
              <div className="mt-4 space-y-2">
                <select data-testid={`inquiry-status-${q.request_id}`} value={INQUIRY_STATUSES.includes(q.status)?q.status:"New"} onChange={(e)=>update(q.request_id, { status: e.target.value })}
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm outline-none focus:border-gold">
                  {INQUIRY_STATUSES.map((s)=><option key={s}>{s}</option>)}
                </select>
                <textarea data-testid={`inquiry-notes-${q.request_id}`} rows={2} defaultValue={q.admin_notes||""} placeholder="Internal notes (private)"
                  onBlur={(e)=>{ if((e.target.value||"")!==(q.admin_notes||"")) update(q.request_id, { admin_notes: e.target.value }); }}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-gold" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Contacts() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/contacts", { params: search ? { search } : {} }).then((r)=>setItems(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [search]);
  useEffect(() => { const t=setTimeout(load,250); return ()=>clearTimeout(t); }, [load]);

  const update = async (id, patch) => {
    try { await api.patch(`/admin/contacts/${id}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input data-testid="contacts-search" placeholder="Search messages…" value={search} onChange={(e)=>setSearch(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-2.5 outline-none focus:border-gold" />
      </div>
      {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div> :
      !items.length ? <div className="py-16 text-center text-slate-400" data-testid="contacts-empty">No messages found.</div> : (
        <div className="grid gap-4 lg:grid-cols-2">
          {items.map((c) => (
            <div key={c.id} data-testid={`contact-card-${c.message_id || c.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold text-charcoal">{c.message_id || "—"}</span>
                <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-2 font-semibold">{c.name}</div>
              <div className="text-sm text-slate-500">{c.email} {c.phone && `· ${c.phone}`}</div>
              <p className="mt-3 text-sm text-slate-600">{c.message}</p>
              <div className="mt-3"><EmailStatus record={c} /></div>
              {c.message_id && (
                <div className="mt-4 space-y-2">
                  <select data-testid={`contact-status-${c.message_id}`} value={CONTACT_STATUSES.includes(c.status)?c.status:"New"} onChange={(e)=>update(c.message_id, { status: e.target.value })}
                    className="rounded-full border border-slate-200 px-4 py-2 text-sm outline-none focus:border-gold">
                    {CONTACT_STATUSES.map((s)=><option key={s}>{s}</option>)}
                  </select>
                  <textarea data-testid={`contact-notes-${c.message_id}`} rows={2} defaultValue={c.admin_notes||""} placeholder="Internal notes (private)"
                    onBlur={(e)=>{ if((e.target.value||"")!==(c.admin_notes||"")) update(c.message_id, { admin_notes: e.target.value }); }}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-gold" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Content() {
  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState({});
  const [testimonials, setTestimonials] = useState([]);
  const [newT, setNewT] = useState({ name:"", event_type:"", quote:"", rating:5, published:true });

  const load = useCallback(() => {
    api.get("/content/packages").then((r)=>setPackages(r.data)).catch(()=>{});
    api.get("/content/settings").then((r)=>setSettings(r.data)).catch(()=>{});
    api.get("/admin/testimonials").then((r)=>setTestimonials(r.data)).catch(()=>{});
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePackage = async (meals, price) => {
    try { await api.patch(`/admin/packages/${meals}`, { price }); toast.success("Package updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveSettings = async () => {
    try { await api.patch("/admin/settings", settings); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addTestimonial = async () => {
    if (!newT.name || !newT.quote) { toast.error("Name and quote required"); return; }
    try { await api.post("/admin/testimonials", newT); toast.success("Testimonial added"); setNewT({ name:"", event_type:"", quote:"", rating:5, published:true }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteTestimonial = async (id) => {
    try { await api.delete(`/admin/testimonials/${id}`); load(); } catch {}
  };

  const cls = "rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-gold";

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <MenuManagement />
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="font-serif text-xl text-charcoal mb-4">Meal Prep Packages</h3>
        <div className="space-y-3">
          {packages.map((p) => (
            <div key={p.meals} className="flex items-center gap-3">
              <span className="w-24 text-sm font-medium">{p.meals} meals</span>
              <input data-testid={`package-price-${p.meals}`} defaultValue={p.price} placeholder="e.g. $80 or leave blank"
                className={`${cls} flex-1`} onBlur={(e)=>savePackage(p.meals, e.target.value)} />
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-400">Leave blank to keep "Pricing provided at review". Saves on blur.</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="font-serif text-xl text-charcoal mb-4">Schedule & Service Area</h3>
        <div className="space-y-3">
          <label className="block"><span className="text-sm">Order Deadline</span>
            <input data-testid="settings-deadline" className={`${cls} w-full mt-1`} value={settings.order_deadline||""} onChange={(e)=>setSettings({...settings,order_deadline:e.target.value})} /></label>
          <label className="block"><span className="text-sm">Fulfillment Days</span>
            <input data-testid="settings-fulfillment" className={`${cls} w-full mt-1`} value={settings.fulfillment_days||""} onChange={(e)=>setSettings({...settings,fulfillment_days:e.target.value})} /></label>
          <label className="block"><span className="text-sm">Service Area</span>
            <input data-testid="settings-service-area" className={`${cls} w-full mt-1`} value={settings.service_area||""} onChange={(e)=>setSettings({...settings,service_area:e.target.value})} /></label>
        </div>
        <button data-testid="settings-save" onClick={saveSettings} className="mt-4 rounded-full bg-gold px-6 py-2.5 font-semibold text-charcoal hover:bg-gold-bright">Save Settings</button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
        <h3 className="font-serif text-xl text-charcoal mb-4">Testimonials</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <input data-testid="testimonial-name" placeholder="Customer name" className={cls} value={newT.name} onChange={(e)=>setNewT({...newT,name:e.target.value})} />
          <input data-testid="testimonial-event" placeholder="Event type" className={cls} value={newT.event_type} onChange={(e)=>setNewT({...newT,event_type:e.target.value})} />
          <textarea data-testid="testimonial-quote" placeholder="Testimonial quote" className={`${cls} sm:col-span-2`} rows={2} value={newT.quote} onChange={(e)=>setNewT({...newT,quote:e.target.value})} />
        </div>
        <button data-testid="testimonial-add" onClick={addTestimonial} className="rounded-full bg-charcoal px-6 py-2.5 font-semibold text-white">Add Testimonial</button>
        <div className="mt-5 space-y-3">
          {testimonials.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-4">
              <div>
                <div className="flex items-center gap-1">{Array.from({length:t.rating||5}).map((_,i)=><Star key={i} className="w-3.5 h-3.5 fill-gold text-gold" />)}</div>
                <div className="mt-1 text-sm italic text-slate-600">"{t.quote}"</div>
                <div className="mt-1 text-xs font-semibold">{t.name} · {t.event_type}</div>
              </div>
              <button data-testid={`testimonial-delete-${t.id}`} onClick={()=>deleteTestimonial(t.id)} className="text-crimson hover:bg-crimson/10 rounded-lg p-2"><X className="w-4 h-4" /></button>
            </div>
          ))}
          {!testimonials.length && <p className="text-sm text-slate-400">No testimonials yet.</p>}
        </div>
      </div>
    </div>
  );
}

function FridayOrders({ onChange }) {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status !== "All") params.status = status;
    api.get("/admin/friday-orders", { params }).then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [search, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-8">
      <FridayWeekManager />
      <div>
        <h3 className="font-serif text-xl text-charcoal mb-4">Friday Plate Orders</h3>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="friday-orders-search" placeholder="Search by Order ID, name, email, phone…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-2.5 outline-none focus:border-gold" />
          </div>
          <select data-testid="friday-orders-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 outline-none focus:border-gold">
            <option>All</option>
            {FRIDAY_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <button onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-600"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div> :
          orders.length === 0 ? <div className="py-16 text-center text-slate-400" data-testid="friday-orders-empty">No Friday Plate orders yet.</div> : (
            <div className="overflow-x-auto thin-scroll rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Order ID</th><th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">Friday</th><th className="px-4 py-3 font-medium">Total</th>
                    <th className="px-4 py-3 font-medium">Fulfillment</th><th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id} data-testid={`friday-order-row-${o.order_id}`} onClick={() => setSelected(o.order_id)} className="border-t border-slate-100 hover:bg-bone cursor-pointer">
                      <td className="px-4 py-3 font-mono font-semibold text-charcoal">{o.order_id}</td>
                      <td className="px-4 py-3">{o.first_name} {o.last_name}<div className="text-xs text-slate-400">{o.email}</div></td>
                      <td className="px-4 py-3 text-slate-600">{o.friday_date || "—"}</td>
                      <td className="px-4 py-3">${Number(o.order_total || 0).toFixed(2)}</td>
                      <td className="px-4 py-3">{o.fulfillment_method}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLOR[o.status] || "bg-slate-100 text-slate-700"}`}>{o.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      {selected && <FridayOrderDrawer orderId={selected} onClose={() => setSelected(null)} onSaved={() => { load(); onChange && onChange(); }} />}
    </div>
  );
}

function FridayOrderDrawer({ orderId, onClose, onSaved }) {
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [flow, setFlow] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const applyOrder = (d) => { setOrder(d); setForm({ status: d.status, invoice_status: d.invoice_status, payment_status: d.payment_status, admin_notes: d.admin_notes ?? "" }); };
  const reload = useCallback(() => api.get(`/admin/friday-orders/${orderId}`).then((r) => applyOrder(r.data)).catch(() => {}), [orderId]);
  useEffect(() => { reload(); }, [reload]);

  const pending = order?.pending_notification;
  const secondsLeft = pending ? Math.max(0, Math.ceil((new Date(pending.send_at).getTime() - nowTick) / 1000)) : 0;
  useEffect(() => {
    if (!pending) return;
    const tick = setInterval(() => setNowTick(Date.now()), 1000);
    const poll = setInterval(() => { reload(); }, 4000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [pending, reload]);

  const onStatusSelect = async (v) => {
    if (FRIDAY_CUSTOMER_FACING.includes(v) && v !== order.status) {
      setFlow({ step: "review", loading: true });
      try {
        const r = await api.post(`/admin/friday-orders/${orderId}/status-change/preview`, { new_status: v });
        setFlow({ step: "review", preview: r.data, verified: false });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setFlow(null); }
    } else { setForm({ ...form, status: v }); }
  };
  const commitStatus = async () => {
    const p = flow.preview;
    setFlow({ ...flow, submitting: true, error: null });
    try {
      await api.post(`/admin/friday-orders/${orderId}/status-change/commit`, { new_status: p.new_status, payment_verified: !!flow.verified });
      setFlow(null); toast.success(`Order marked ${p.new_status}. Customer email will send in ${p.grace_seconds}s — you can still UNDO.`);
      await reload(); onSaved && onSaved();
    } catch (e) { setFlow({ ...flow, submitting: false, error: formatApiError(e.response?.data?.detail) }); }
  };
  const undo = async () => {
    try { await api.post(`/admin/friday-orders/${orderId}/status-change/undo`); toast.success("Status change undone. No email was sent."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    await reload(); onSaved && onSaved();
  };
  const retryEmail = async () => {
    if (!window.confirm("Resend the customer email for this status?")) return;
    try { await api.post(`/admin/friday-orders/${orderId}/status-change/retry`); toast.success("Customer email resent."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    await reload(); onSaved && onSaved();
  };
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/admin/friday-orders/${orderId}`, form); toast.success("Order updated"); onSaved(); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const mailto = order ? `mailto:${order.email}?subject=${encodeURIComponent(`Taste of Platinum — Friday Plate Order ${order.order_id}`)}` : "#";
  const history = order?.status_history || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="friday-order-drawer">
      <div className="absolute inset-0 bg-charcoal/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-bone h-full overflow-y-auto thin-scroll shadow-2xl animate-fade-in">
        <div className="sticky top-0 z-10 bg-charcoal text-white px-6 py-4 flex items-center justify-between">
          <div><div className="font-mono font-semibold">{orderId}</div><div className="text-xs text-platinum-dark">Friday Plate order</div></div>
          <button data-testid="friday-drawer-close" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        {!order ? <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div> : (
          <div className="p-6 space-y-6">
            {pending && (
              <div data-testid="friday-pending-banner" className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4">
                <div className="flex items-center gap-2 text-amber-900 font-semibold"><Clock className="w-5 h-5" /> Order marked {order.status}.</div>
                <p className="mt-1 text-sm text-amber-800">Customer Notification: <strong data-testid="friday-pending-countdown">Pending — sends in {secondsLeft} second{secondsLeft === 1 ? "" : "s"}</strong></p>
                <button data-testid="friday-undo-status-change" onClick={undo} className="mt-3 inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-sm font-semibold text-white"><Undo2 className="w-4 h-4" /> UNDO</button>
              </div>
            )}
            <Section title="Customer">
              <Row k="Name" v={`${order.first_name} ${order.last_name}`} />
              <Row k="Email" v={order.email} /><Row k="Phone" v={order.phone} />
              <Row k="Friday" v={order.friday_date || "—"} />
              <a data-testid="friday-email-customer" href={mailto} className="inline-flex items-center gap-2 mt-1 rounded-full border border-slate-300 px-4 py-2 text-sm text-charcoal hover:bg-bone"><Mail className="w-4 h-4" /> Email Customer</a>
            </Section>
            <Section title="Order">
              {(order.items || []).map((it, i) => (
                <div key={i} data-testid={`friday-drawer-item-${i}`} className="rounded-lg bg-bone p-2.5 text-sm">
                  <div className="font-semibold text-charcoal">{it.name}{it.size ? ` — ${it.size}` : ""} × {it.quantity} — ${(it.price * it.quantity).toFixed(2)}</div>
                  <div className="text-slate-500 text-xs">Price per plate: ${Number(it.price).toFixed(2)}</div>
                  {it.prep && <div className="text-slate-500 text-xs">Preparation: {it.prep}</div>}
                  {it.rice && <div className="text-slate-500 text-xs">Rice: {it.rice}</div>}
                  {it.sides?.length > 0 && <div className="text-slate-500 text-xs"><span className="font-medium text-charcoal">Standard included:</span> {it.sides.join(", ")}</div>}
                  <div className="text-slate-500 text-xs"><span className="font-medium text-charcoal">Paid add-ons:</span> {it.addons?.length ? it.addons.map((a) => `${a.name} ×${a.quantity}${a.flavor ? ` — Flavor: ${a.flavor}` : ""}`).join(", ") : "None"}</div>
                </div>
              ))}
              <Row k="Total" v={`$${Number(order.order_total || 0).toFixed(2)}`} />
              <Row k="Fulfillment" v={order.fulfillment_method} />
              {order.delivery && <Row k="Delivery" v={`${order.delivery.address} ${order.delivery.apt}, ${order.delivery.city} ${order.delivery.zip} (${order.delivery.window || "flexible"})`} />}
              {order.notes && <Row k="Notes" v={order.notes} />}
              <Row k="Submitted" v={new Date(order.created_at).toLocaleString()} />
            </Section>
            <Section title="Notifications">
              <div className="flex items-center justify-between gap-3 flex-wrap"><span className="text-sm text-slate-600">Customer status email:</span><StatusNotifPill order={order} secondsLeft={secondsLeft} /></div>
              {order.status_notification === "Failed" && !pending && (
                <button data-testid="friday-retry-status-email" onClick={retryEmail} className="mt-3 inline-flex items-center gap-2 rounded-full border border-red-300 text-red-700 px-4 py-2 text-sm hover:bg-red-50"><RotateCcw className="w-4 h-4" /> Retry Email</button>
              )}
              <div className="mt-3 border-t border-slate-100 pt-3"><EmailStatus record={order} /></div>
            </Section>
            {history.length > 0 && (
              <Section title="Status History">
                <div className="space-y-2" data-testid="friday-status-history">
                  {[...history].reverse().map((h) => (
                    <div key={h.id} className="rounded-lg bg-bone p-2.5 text-xs text-slate-600">
                      <div className="text-slate-400">{new Date(h.at).toLocaleString()} · {h.admin || "admin"}</div>
                      <div className="font-medium text-charcoal">{h.previous_status} → {h.new_status}</div>
                      <div>{h.cancelled ? "Action cancelled during undo period · Customer notification: Not Sent" : `Customer notification: ${h.email_status || "Pending"}`}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            <Section title="Manage">
              <div className="space-y-3">
                <Select label="Order Status" testid="friday-drawer-status" value={form.status} options={FRIDAY_STATUSES} disabled={!!pending} onChange={onStatusSelect} />
                {!pending && <p className="text-xs text-slate-400 -mt-1">Paid, Confirmed, Ready for Pickup and Out for Delivery require a guided confirmation before any customer email.</p>}
                <Select label="Invoice Status" testid="friday-drawer-invoice-status" value={form.invoice_status} options={FRIDAY_INVOICE_STATUSES} onChange={(v) => setForm({ ...form, invoice_status: v })} />
                <Select label="Payment Status" testid="friday-drawer-payment-status" value={form.payment_status} options={FRIDAY_PAYMENT_STATUSES} onChange={(v) => setForm({ ...form, payment_status: v })} />
                <label className="block"><span className="text-sm font-medium text-charcoal">Admin Notes</span>
                  <textarea data-testid="friday-drawer-admin-notes" rows={3} className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-gold" value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} /></label>
              </div>
              <button data-testid="friday-drawer-save" onClick={save} disabled={saving} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 font-semibold text-charcoal hover:bg-gold-bright disabled:opacity-60">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save Changes"}
              </button>
            </Section>
          </div>
        )}
      </div>
      {flow && <StatusChangeModal flow={flow} setFlow={setFlow} onCommit={commitStatus} onClose={() => setFlow(null)} />}
    </div>
  );
}

function FridayWeekManager() {
  const [week, setWeek] = useState(null);
  const [plates, setPlates] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const weeks = (await api.get("/admin/friday-weeks").then((r) => r.data).catch(() => [])) || [];
    const cur = weeks.find((w) => w.is_current) || weeks[0] || null;
    setWeek(cur);
    if (cur) api.get("/admin/friday-plates", { params: { week_id: cur.id } }).then((r) => setPlates(r.data)).catch(() => {});
    else setPlates([]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveWeek = async () => {
    try { await api.patch(`/admin/friday-weeks/${week.id}`, week); toast.success("Week saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const duplicate = async () => {
    try { const r = await api.post(`/admin/friday-weeks/${week.id}/duplicate`); toast.success("Duplicated to a new (draft) week. Set it current when ready."); await api.patch(`/admin/friday-weeks/${r.data.id}`, { is_current: false }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const savePlate = async (p) => {
    try {
      const addons = (p.addonsText || "").split(",").map((s) => s.trim()).filter(Boolean).map((s) => { const [name, price] = s.split(":"); return { name: (name || "").trim(), price: Number(price) || 0 }; });
      const sizes = (p.sizes || []).map((s, i) => ({ id: s.id || `sz-${Date.now()}-${i}`, name: (s.name || "").trim(), price: Number(s.price) || 0, active: s.active !== false, display_order: Number(s.display_order) || i })).filter((s) => s.name);
      const prep_options = (p.prepText || "").split(",").map((s) => s.trim()).filter(Boolean);
      const payload = { name: p.name, description: p.description || "", price: Number(p.price) || 0, included: p.included || "", addons, sizes, prep_options, status: p.status || "Available", display_order: Number(p.display_order) || 0 };
      if (p.id) await api.patch(`/admin/friday-plates/${p.id}`, payload);
      else await api.post("/admin/friday-plates", { ...payload, week_id: week.id });
      toast.success(p.id ? "Plate updated" : "Plate added"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setPlateStatus = async (p, status) => {
    try { await api.patch(`/admin/friday-plates/${p.id}`, { status }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removePlate = async (p) => {
    if (!window.confirm(`Remove ${p.name} from this Friday menu?`)) return;
    try { await api.delete(`/admin/friday-plates/${p.id}`); toast.success("Plate removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!week) return <div data-testid="friday-week-manager" className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-500">No Friday week configured yet.</div>;
  const cls = "rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-gold";
  return (
    <div data-testid="friday-week-manager" className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-serif text-xl text-charcoal">This Week's Friday Plates</h3>
        <button data-testid="fw-duplicate" onClick={duplicate} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm text-charcoal hover:bg-bone"><Copy className="w-4 h-4" /> Duplicate Previous</button>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
        <label>Friday Date<input data-testid="fw-friday-date" type="date" className={`${cls} w-full mt-1`} value={week.friday_date || ""} onChange={(e) => setWeek({ ...week, friday_date: e.target.value })} /></label>
        <label>Ordering Deadline<input data-testid="fw-deadline" className={`${cls} w-full mt-1`} value={week.order_deadline || ""} onChange={(e) => setWeek({ ...week, order_deadline: e.target.value })} /></label>
        <label className="sm:col-span-2">Weekly Announcement<input data-testid="fw-announcement" className={`${cls} w-full mt-1`} value={week.announcement || ""} onChange={(e) => setWeek({ ...week, announcement: e.target.value })} /></label>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2"><input data-testid="fw-ordering-open" type="checkbox" className="w-4 h-4 accent-gold" checked={!!week.ordering_open} onChange={(e) => setWeek({ ...week, ordering_open: e.target.checked })} /> Ordering Open</label>
        <label className="flex items-center gap-2"><input data-testid="fw-pickup" type="checkbox" className="w-4 h-4 accent-gold" checked={!!week.pickup_available} onChange={(e) => setWeek({ ...week, pickup_available: e.target.checked })} /> Pickup Available</label>
        <label className="flex items-center gap-2"><input data-testid="fw-delivery" type="checkbox" className="w-4 h-4 accent-gold" checked={!!week.delivery_available} onChange={(e) => setWeek({ ...week, delivery_available: e.target.checked })} /> Delivery Available</label>
        <label className="flex items-center gap-2"><input data-testid="fw-current" type="checkbox" className="w-4 h-4 accent-gold" checked={!!week.is_current} onChange={(e) => setWeek({ ...week, is_current: e.target.checked })} /> Current Week</label>
      </div>
      <button data-testid="fw-save" onClick={saveWeek} className="mt-4 inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-sm font-semibold text-white"><Save className="w-4 h-4" /> Save Week</button>

      <div className="mt-6 flex items-center justify-between">
        <h4 className="font-semibold text-charcoal">Plates</h4>
        <button data-testid="fplate-add" onClick={() => setEditing({ name: "", description: "", price: "", included: "", addonsText: "", prepText: "", sizes: [], status: "Available", display_order: plates.length + 1 })} className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-charcoal hover:bg-gold-bright"><Plus className="w-4 h-4" /> Add Plate</button>
      </div>
      <div className="mt-3 space-y-2">
        {plates.map((p) => (
          <div key={p.id} data-testid={`fplate-row-${p.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
            <div><div className="font-semibold text-charcoal">{p.name} <span className="text-slate-400 font-normal">{p.sizes && p.sizes.length ? `${p.sizes.length} size${p.sizes.length > 1 ? "s" : ""}` : `$${Number(p.price).toFixed(2)}`}</span></div>
              <div className="text-xs text-slate-400">{p.status}{p.included ? ` · ${p.included}` : ""}</div></div>
            <div className="flex items-center gap-1">
              <button data-testid={`fplate-soldout-${p.id}`} title="Toggle Sold Out" onClick={() => setPlateStatus(p, p.status === "Sold Out" ? "Available" : "Sold Out")} className="rounded-lg px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50">Sold Out</button>
              <button data-testid={`fplate-hide-${p.id}`} title={p.status === "Hidden" ? "Show" : "Hide"} onClick={() => setPlateStatus(p, p.status === "Hidden" ? "Available" : "Hidden")} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100">{p.status === "Hidden" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
              <button data-testid={`fplate-edit-${p.id}`} onClick={() => setEditing({ ...p, addonsText: (p.addons || []).map((a) => `${a.name}:${a.price}`).join(", "), prepText: (p.prep_options || []).join(", "), sizes: (p.sizes || []).map((s) => ({ ...s })) })} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`fplate-remove-${p.id}`} onClick={() => removePlate(p)} className="rounded-lg p-2 text-crimson hover:bg-crimson/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {plates.length === 0 && <p className="text-sm text-slate-400">No plates yet. Add this week's plates.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="fplate-modal">
          <div className="absolute inset-0 bg-charcoal/70" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="bg-charcoal text-white px-6 py-4"><h3 className="font-serif text-lg">{editing.id ? "Edit Plate" : "Add Plate"}</h3></div>
            <div className="p-6 space-y-3 text-sm">
              <label className="block">Plate Name<input data-testid="fplate-name" className={`${cls} w-full mt-1`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
              <label className="block">Description<textarea data-testid="fplate-desc" rows={2} className={`${cls} w-full mt-1`} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></label>
              <label className="block">Included Components<input data-testid="fplate-included" className={`${cls} w-full mt-1`} value={editing.included} onChange={(e) => setEditing({ ...editing, included: e.target.value })} /></label>
              <div className="flex gap-3">
                <label className="block flex-1">Price ($)<input data-testid="fplate-price" type="number" step="0.01" className={`${cls} w-full mt-1`} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></label>
                <label className="block flex-1">Display Order<input data-testid="fplate-display-order" type="number" className={`${cls} w-full mt-1`} value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: e.target.value })} /></label>
              </div>
              <label className="block">Add-ons (name:price, comma separated)<input data-testid="fplate-addons" className={`${cls} w-full mt-1`} placeholder="Extra Rice & Peas:6, Festival:6" value={editing.addonsText} onChange={(e) => setEditing({ ...editing, addonsText: e.target.value })} /></label>
              <label className="block">Preparation Options <span className="text-xs font-normal text-slate-400">(comma separated — e.g. Fried, Escovitch, Steamed. Leave blank if none)</span><input data-testid="fplate-prep" className={`${cls} w-full mt-1`} placeholder="Fried, Escovitch, Steamed" value={editing.prepText || ""} onChange={(e) => setEditing({ ...editing, prepText: e.target.value })} /></label>
              <div>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-charcoal">Plate Sizes <span className="text-xs font-normal text-slate-400">(optional — leave empty to use the single price above)</span></span>
                  <button data-testid="fplate-add-size" onClick={() => setEditing({ ...editing, sizes: [...(editing.sizes || []), { id: `sz-${Date.now()}`, name: "", price: "", active: true, display_order: (editing.sizes || []).length + 1 }] })} className="text-xs font-semibold text-charcoal border-b border-gold">+ Add Size</button>
                </div>
                <div className="mt-2 space-y-2">
                  {(editing.sizes || []).map((s, i) => (
                    <div key={i} data-testid={`fplate-size-row-${i}`} className="flex items-center gap-2">
                      <input data-testid={`fplate-size-name-${i}`} placeholder="Size (e.g. Large)" className={`${cls} flex-1 ${s.active === false ? "opacity-50" : ""}`} value={s.name} onChange={(e) => { const sz = [...editing.sizes]; sz[i] = { ...sz[i], name: e.target.value }; setEditing({ ...editing, sizes: sz }); }} />
                      <input data-testid={`fplate-size-price-${i}`} type="number" step="0.01" placeholder="$" className={`${cls} w-24`} value={s.price} onChange={(e) => { const sz = [...editing.sizes]; sz[i] = { ...sz[i], price: e.target.value }; setEditing({ ...editing, sizes: sz }); }} />
                      <button data-testid={`fplate-size-active-${i}`} title={s.active !== false ? "Hide size" : "Show size"} onClick={() => { const sz = [...editing.sizes]; sz[i] = { ...sz[i], active: !(s.active !== false) }; setEditing({ ...editing, sizes: sz }); }} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg">{s.active !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                      <button data-testid={`fplate-size-remove-${i}`} onClick={() => setEditing({ ...editing, sizes: editing.sizes.filter((_, j) => j !== i) })} className="p-2 text-crimson hover:bg-crimson/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {(editing.sizes || []).length === 0 && <p className="text-xs text-slate-400">No sizes — customers pay the single price above.</p>}
                </div>
              </div>
              <label className="block">Status<select data-testid="fplate-status" className={`${cls} w-full mt-1`} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>{FRIDAY_PLATE_STATES.map((s) => <option key={s}>{s}</option>)}</select></label>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button data-testid="fplate-cancel" onClick={() => setEditing(null)} className="flex-1 rounded-full border border-slate-300 px-5 py-2.5 font-semibold text-charcoal hover:bg-bone">Cancel</button>
              <button data-testid="fplate-save" onClick={() => savePlate(editing)} className="flex-1 rounded-full bg-gold px-5 py-2.5 font-semibold text-charcoal hover:bg-gold-bright">Save Plate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailStatus({ record }) {
  const pill = (s) => {
    const v = s || "Pending";
    const cls = v === "Sent" ? "bg-green-100 text-green-800" : v === "Failed" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500";
    return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{v}</span>;
  };
  return (
    <div data-testid="email-status" className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
      <span className="flex items-center gap-1.5">Customer email: {pill(record.customer_email_status)}</span>
      <span className="flex items-center gap-1.5">Business email: {pill(record.business_email_status)}</span>
      {record.email_last_attempt && <span>· Last attempt {new Date(record.email_last_attempt).toLocaleString()}</span>}
    </div>
  );
}

function Section({ title, children }) {
  return (<div><div className="eyebrow text-crimson mb-3">{title}</div><div className="rounded-2xl bg-white border border-slate-200 p-4 space-y-2">{children}</div></div>);
}
function Row({ k, v }) {
  return (<div className="flex gap-3 text-sm"><span className="w-32 shrink-0 text-slate-400">{k}</span><span className="text-charcoal break-words">{v}</span></div>);
}
function Select({ label, value, options, onChange, testid, disabled }) {
  return (<label className="block"><span className="text-sm font-medium text-charcoal">{label}</span>
    <select data-testid={testid} value={value} disabled={disabled} onChange={(e)=>onChange(e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-gold disabled:bg-slate-100 disabled:text-slate-400">
      {options.map((o)=><option key={o}>{o}</option>)}
    </select></label>);
}
